import {Component, Host, Input, OnInit, ViewChild} from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { FormConfig } from '../../../../models/form-config';
import { FormFactoryService } from '../../../../services/form-factory.service';
import { SectionComponent } from '../../../../components/section/section.component';
import { OwnerDataSectionComponent } from '../owner-data-section.component';
import { PredefinedValuesService } from '../../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import { OwnerDataStateService } from '../../../../../core/services/impl/section-services/owner-data/owner-data-state.service';
import { PredefinedValuesStateService } from '../../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import { CustomMetricsModel } from '../../../../components/models/metrics/custom-metrics.model';
import { Memoize } from '../../../../utils/memoize';
import { FieldConfig } from '../../../../models/field-config';
import { AdditionalDriverConfig } from './additional-driver.config';
import { AdditionalDriverModel } from '../../../../../core/models/owner-data/additional-driver.model';
import { getDrivingLicenseIssueYears, getDrivingLicenseIssueMonths } from '../../../../utils/driving-license-years';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { OwnerDataSectionConfig } from '../owner-data-section.config';
import {MaterialYearPickerComponent} from '../../../../components/fields/datepickers/material-datepicker/material-year-picker/material-year-picker.component';
import * as moment from 'moment';

@Component({
  selector: 'app-additional-driver',
  templateUrl: './additional-driver.component.html'
})
export class AdditionalDriverComponent extends AdditionalDriverConfig implements OnInit {

  public drivingLicenseIssueMonthCollected: boolean = true;
  public dataPresentationFields: string[];

  @Input() bean: AdditionalDriverModel;
  @Input() isDirtyBean: any;
  @Input() index: number;
  @Input() additionalDriver: AdditionalDriverModel;
  @Input() additionalDriverFormGroup: FormGroup;
  @Input() additionalDriverFormConfig: FormConfig;
  @Input() additionalDriverFormService: FormFactoryService;
  @Input() parentSection: SectionComponent;

  @ViewChild('drivingLicenseIssueYearField') drivingLicenseIssueYearField: MaterialYearPickerComponent;

  constructor(@Host() public parent: OwnerDataSectionComponent,
              public predefined: PredefinedValuesService,
              public stateService: OwnerDataStateService,
              public predefinedState: PredefinedValuesStateService) {
    super(predefined, stateService);
  }

  ngOnInit() {
    this.initControlChanges();
    this.updateDrivingLicenseIssueYearDictionary();
    this.updateDrivingLicenseIssueMonthState();
    this.prepareFieldsInPreviewMode();
  }

  customMetricsData(count): CustomMetricsModel {
    return {
      sectionKey: 'ADDITIONAL_DRIVERS_DATA',
      count: count
    }
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = this.addIndexToKey(event);
  }

  @Memoize()
  getAdditionalDriverKeyWithIndex(key, index): string {
    return `${this.additionalDriverFormConfig.formKey}.${key}.${index}`;
  }

  getFieldConfig(fieldKey: string): FieldConfig {
    return this.additionalDriverFormService.fieldConfig(fieldKey);
  }

  getControl(key: string): AbstractControl {
    return this.additionalDriverFormGroup.controls[key];
  }

  get previewMode(): boolean {
    return this.parentSection && this.parentSection.isPreviewMode;
  }

  canFieldDisplayed(fieldKey: string): boolean {
    return ((this.previewMode && this.getFieldConfig(fieldKey).displayInEditableMode) || !this.previewMode);
  }

  private addIndexToKey(event) {
    return Object.keys(event).map(key => ({[key + `.${this.index}`]: true}))[0]
  }

  private initControlChanges(): void {

    this.handleControlsState(
      this.consts.BIRTH_DATE,
      this.consts.DRIVING_LICENSE_ISSUE_YEAR,
      this.consts.DRIVING_LICENSE_ISSUE_MONTH,
    );
  }

  private handleControlsState(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(controlName => {
      this.additionalDriverFormGroup.controls[controlName].valueChanges.subscribe(value => {
        if (controlName === this.consts.DRIVING_LICENSE_ISSUE_YEAR) {
          value = moment(value).year();
        }
        this.additionalDriver[controlName] = value;

        if (controlName === this.consts.BIRTH_DATE) {
          this.updateDrivingLicenseIssueYearDictionary();
          this.drivingLicenseIssueYearField.initDateRange();
        }
        if (controlName === this.consts.DRIVING_LICENSE_ISSUE_YEAR) {
          this.updateDrivingLicenseIssueMonthState();
        }
        this.stateService.updateCalculationData();
      });
    });
  }

  private updateDrivingLicenseIssueYearDictionary(): void {
    const birthDate = this.additionalDriver.birthDate ? new Date(this.additionalDriver.birthDate) : null;
    const startYear = isNotNullOrUndefined(birthDate) ? (birthDate.getFullYear() + this.DRIVER_MIN_AGE) : OwnerDataSectionConfig.START_YEAR;
    const dictionary = getDrivingLicenseIssueYears(startYear);
    const minYear = Math.min(...dictionary);
    const maxYear = Math.max(...dictionary);
    this.getFieldConfig(this.consts.DRIVING_LICENSE_ISSUE_YEAR).materialDateFieldConfig.minDate = minYear + '';
    this.getFieldConfig(this.consts.DRIVING_LICENSE_ISSUE_YEAR).materialDateFieldConfig.maxDate = maxYear + '';
    if (!dictionary.includes(this.additionalDriver.drivingLicenseIssueYear)) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_YEAR).reset();
    }
  }

  private handleDrivingLicenseIssueYearChange(pesel: string) {
    this.updateOptionsOnValidPesel(pesel, this.additionalDriverFormConfig);
    this.resetAndSetPreviousPeselValue(pesel, this.additionalDriverFormGroup);
  }

  private updateDrivingLicenseIssueMonthState(): void {
    const drivingLicenseIssueYear = this.additionalDriver.drivingLicenseIssueYear;

    if (this.stateService.shouldCollectDrivingLicenseIssueMonth(this.additionalDriver.drivingLicenseIssueYear)) {
      this.enableCollectingDrivingLicenseIssueMonth();
    } else {
      this.disableCollectingDrivingLicenseIssueMonth();
    }

    if (this.drivingLicenseIssueMonthCollected) {
      const items = getDrivingLicenseIssueMonths(drivingLicenseIssueYear);
      this.getFieldConfig(this.consts.DRIVING_LICENSE_ISSUE_MONTH).options['items'] = items;
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).setValidators([Validators.required]);
      
      // validate existing value
      const month = this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).value;
      if (isNotNullOrUndefined(month) && !items.find(item => item.key === month)) { // month is defined but it doesn't exist in current dictionary
        this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset();
      }
    } else {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset();
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).clearValidators();
    }
    this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).updateValueAndValidity();
  }

  private prepareFieldsInPreviewMode(): void {
    this.dataPresentationFields = [
      this.consts.BIRTH_DATE,
      this.consts.DRIVING_LICENSE_ISSUE_YEAR,
    ];

    if (this.additionalDriver.drivingLicenseIssueMonth) {
      this.dataPresentationFields.push(this.consts.DRIVING_LICENSE_ISSUE_MONTH);
    }
  }

  private disableCollectingDrivingLicenseIssueMonth(): void {
    this.drivingLicenseIssueMonthCollected = false;
    if (isNotNullOrUndefined(this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH))) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).reset(null);
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).clearValidators();
    }
  }

  private enableCollectingDrivingLicenseIssueMonth(): void {
    this.drivingLicenseIssueMonthCollected = true;
    if (isNotNullOrUndefined(this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH))) {
      this.getControl(this.consts.DRIVING_LICENSE_ISSUE_MONTH).setValidators([Validators.required]);
    }
  }
}
